import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AuthHeader from "./AuthHeader";

// Create dedicated message components with forced styles
const ErrorMessage = ({ message }) => (
  <div
    style={{
      backgroundColor: "#ffebee",
      color: "#c62828",
      padding: "12px 16px",
      borderRadius: "4px",
      marginBottom: "20px",
      fontSize: "14px",
      textAlign: "center",
      border: "2px solid #f44336",
    }}
  >
    {message}
  </div>
);

const SuccessMessage = ({ message }) => (
  <div
    style={{
      backgroundColor: "#e8f5e9",
      color: "#2e7d32",
      padding: "12px 16px",
      borderRadius: "4px",
      marginBottom: "20px",
      fontSize: "14px",
      fontWeight: "500",
      textAlign: "center",
      border: "2px solid #4caf50",
      boxShadow: "0 0 0 1px #e8f5e9",
    }}
  >
    {message}
  </div>
);

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is already logged in and redirect if so
  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  // Process incoming messages with detailed debugging
  useEffect(() => {
    console.log("Login: Initial render with location state:", location.state);

    // Reset message states to avoid stale messages
    setError("");
    setSuccessMessage("");

    // Process any incoming messages from navigation state
    if (location.state?.message) {
      const message = location.state.message;
      console.log("Login: Received message:", message);

      // Check if it's a registration success message
      if (message.toLowerCase().includes("registration successful")) {
        setSuccessMessage(message);
      } else {
        setError(message);
      }
    }
  }, [location]);

  // Log changes to message states
  useEffect(() => {
    console.log("Login: Current success message:", successMessage);
    console.log("Login: Current error message:", error);
  }, [successMessage, error]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous messages
    setError("");
    setSuccessMessage("");

    if (!email || !password) {
      setError("Please fill in all fields");
      return;
    }

    try {
      setIsLoading(true);
      await signIn(email, password);
      // Navigation will happen through the useEffect that watches the user state
    } catch (err) {
      setError(err.message || "Failed to sign in");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container" style={styles.container}>
      <div style={styles.formWrapper}>
        <AuthHeader />
        <h2 style={styles.title}>Welcome back!</h2>

        {/* Use dedicated components for messages */}
        {error && <ErrorMessage message={error} />}
        {successMessage && <SuccessMessage message={successMessage} />}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label htmlFor="email" style={styles.label}>
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              required
            />
          </div>

          <div style={styles.inputGroup}>
            <label htmlFor="password" style={styles.label}>
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              required
            />
          </div>

          <button type="submit" disabled={isLoading} style={styles.button}>
            {isLoading ? "Logging in..." : "Login"}
          </button>
        </form>
        <p style={styles.signupText}>
          Don't have an account?{" "}
          <Link to="/register" style={styles.link}>
            Register here
          </Link>
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#fff9e9",
    fontFamily: "'Inter', sans-serif",
  },
  formWrapper: {
    backgroundColor: "white",
    padding: "30px",
    borderRadius: "15px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    width: "100%",
    maxWidth: "400px",
  },
  title: {
    textAlign: "center",
    color: "#333",
    marginBottom: "24px",
    fontWeight: "700",
    fontSize: "28px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontWeight: "600",
    color: "#333",
    fontSize: "16px",
  },
  input: {
    padding: "12px 16px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    fontSize: "16px",
    backgroundColor: "#fbfbfb",
    transition: "all 0.3s ease",
    outline: "none",
  },
  button: {
    padding: "14px 20px",
    backgroundColor: "#9CDAED",
    color: "#000",
    fontWeight: "600",
    fontSize: "18px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
    outline: "none",
    marginTop: "10px",
  },
  signupText: {
    marginTop: "24px",
    textAlign: "center",
    color: "#666",
    fontSize: "14px",
  },
  link: {
    color: "#9CDAED",
    textDecoration: "none",
    fontWeight: "600",
  },
};

export default Login;
